import { useState, useMemo } from 'react';
import { Formik } from 'formik';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { trackEvent } from 'lib/analytics';
import { DISTANCE_OPTIONS } from 'constants/distanceOptions';
import zipcodes from 'zipcodes';
import { ARKANSAS_CITIES } from 'constants/arkansasCities';

/* Styles */
import styles from 'styles/login.module.scss';
import layoutStyles from 'styles/layout.module.scss';

/* i18n */
import { useIntl } from 'react-intl';

/* API */
import { apiRequest } from 'lib/API';

/* Material UI and other UI Dependencies */
import { Typography, Autocomplete, Radio, FormControlLabel, RadioGroup, Chip } from '@mui/material';
import OnboardingSteps from 'components/OnboardingSteps';
import OnboardingNavigationButtons from 'components/OnboardingNavigationButtons';
import { Box, FormControl, TextField, FormHelperText } from '@mui/material';
import OnboardingContentWrapper from 'components/OnboardingContentWrapper';

export default function OnboardingLocation() {
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  // Read cached location data for pre-population on back navigation
  const locationQuery = useQuery({
    queryKey: ['/users/me/location/'],
    enabled: false, // Don't fetch, just read from cache
  });
  const locData = locationQuery.data;

  const [cityFilterZip, setCityFilterZip] = useState(() => {
    const zip = queryClient.getQueryData(['/users/me/location/'])?.zip_code || '';
    return /^\d{5}$/.test(zip) ? zip : '';
  });

  const filteredCitiesQuery = useQuery({
    queryKey: [`/cities/us?zipcode=${cityFilterZip}&ordering=name`],
    enabled: !!(cityFilterZip && /^\d{5}$/.test(cityFilterZip)),
  });
  const filteredCities = Array.isArray(filteredCitiesQuery.data) ? filteredCitiesQuery.data : [];

  const intl = useIntl();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;
  const history = useHistory();
  const location = useLocation();

  const handleBack = () => {
    // Determine which experience page to go back to using query param or cached data
    const searchParams = new URLSearchParams(location.search);
    const fromPage = searchParams.get('from');

    if (fromPage === 'education') {
      history.push(`${urlPrefix}/onboarding/education`);
    } else if (fromPage === 'military-experience') {
      history.push(`${urlPrefix}/onboarding/military-experience`);
    } else if (fromPage === 'work-experience') {
      history.push(`${urlPrefix}/onboarding/work-experience`);
    } else {
      // Fallback: Check cached experience data to determine the correct back page
      const cachedExp = queryClient.getQueryData(['/users/me/experience/']);
      if (cachedExp?.jst?.military_experience?.length > 0) {
        history.push(`${urlPrefix}/onboarding/military-experience`);
      } else {
        history.push(`${urlPrefix}/onboarding/work-experience`);
      }
    }
  };

  // Distance options for commute
  const distanceOptions = useMemo(
    () =>
      DISTANCE_OPTIONS.map((opt) =>
        opt.labelId
          ? { id: opt.id, label: intl.formatMessage({ id: opt.labelId }) }
          : {
              id: opt.id,
              label: `${intl.formatMessage({ id: 'onboarding.location.within' })} ${opt.miles} ${intl.formatMessage({
                id: 'onboarding.location.miles',
              })}`,
            }
      ),
    [intl]
  );

  // Auto-derive city from API data when no manual selection has been made.
  // Prefer the city the user previously saved (matched by ID) before falling
  // back to the canonical zipcodes npm lookup.
  const autoCity = useMemo(() => {
    if (!filteredCities.length || !cityFilterZip) return null;
    if (locData?.current_city) {
      const savedIdStr = String(locData.current_city);
      const byId = filteredCities.find((c) => String(c.id) === savedIdStr);
      if (byId) return byId;
    }
    const lookup = zipcodes.lookup(cityFilterZip);
    if (!lookup) return filteredCities[0] || null;
    return filteredCities.find((c) => c.city.toLowerCase() === lookup.city.toLowerCase()) || filteredCities[0] || null;
  }, [filteredCities, cityFilterZip, locData?.current_city]);

  // Derive initial commute option from cached data
  const getInitialCommute = () => {
    if (locData?.desired_commute_mi == null) return null;
    const miles = String(locData.desired_commute_mi);
    return distanceOptions.find((opt) => opt.id === miles) || null;
  };

  // Derive initial alternate cities from cached data
  const getInitialCities = () => {
    if (!locData?.alternate_cities?.length) return [];
    return ARKANSAS_CITIES.filter((city) => locData.alternate_cities.includes(city.id));
  };

  const initialValues = {
    zip_code: locData?.zip_code || '',
    current_city: null,
    desired_commute_mi: getInitialCommute(),
    willing_to_relocate:
      locData?.willing_to_relocate === true ? 'yes' : locData?.willing_to_relocate === false ? 'no' : null,
    alternate_cities: getInitialCities(),
    filed_unemployment_benefits:
      locData?.filed_unemployment_benefits === true
        ? 'yes'
        : locData?.filed_unemployment_benefits === false
        ? 'no'
        : null,
  };

  return (
    <Box sx={{ boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.1)' }}>
      <div className={styles.loginFlow__form__onboarding}>
        <Box px={3}>
          <Typography align="center" variant="h1" gutterBottom sx={{ fontSize: 24, mt: 1 }}>
            {intl.formatMessage({ id: 'onboarding.title' }, { step: 4, totalSteps: 5 })}
          </Typography>
        </Box>
        <Box sx={{ maxWidth: 222, marginLeft: 'auto', marginRight: 'auto', mt: 4, mb: 1 }}>
          <OnboardingSteps activeStep={4} />
        </Box>
      </div>
      <hr className={layoutStyles.separatorBold} />
      <Box className={styles.loadingScreen__cardsContainer}>
        <OnboardingContentWrapper>
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validate={(values) => {
              const errors = {};
              if (!values.zip_code) {
                errors.zip_code = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
              } else if (!/^\d{5}$/.test(values.zip_code)) {
                errors.zip_code = intl.formatMessage({ id: 'experience.form.zipcodeFiveDigits' });
              } else if (!zipcodes.lookup(values.zip_code)) {
                errors.zip_code = intl.formatMessage({ id: 'experience.form.zipcodeInvalidUS' });
              }
              if (!values.desired_commute_mi) {
                errors.desired_commute_mi = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
              }
              if (!values.willing_to_relocate) {
                errors.willing_to_relocate = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
              }
              if (
                values.willing_to_relocate === 'yes' &&
                (!values.alternate_cities || values.alternate_cities.length === 0)
              ) {
                errors.alternate_cities = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
              }
              if (!values.filed_unemployment_benefits) {
                errors.filed_unemployment_benefits = intl.formatMessage({ id: 'jobSearchActivity.form.fieldRequired' });
              }
              return errors;
            }}
            onSubmit={(values) => {
              setSubmitting(true);
              const effectiveCityForSubmit = values.current_city ?? autoCity;
              const valuesToSubmit = {
                zip_code: values.zip_code,
                ...(effectiveCityForSubmit ? { current_city: effectiveCityForSubmit.id } : {}),
                ...(values.desired_commute_mi ? { desired_commute_mi: Number(values.desired_commute_mi.id) } : {}),
                ...(values.willing_to_relocate ? { willing_to_relocate: values.willing_to_relocate === 'yes' } : {}),
                ...(values.willing_to_relocate === 'yes' && values.alternate_cities?.length > 0
                  ? { alternate_cities: values.alternate_cities.map((c) => c.id) }
                  : {}),
                ...(values.filed_unemployment_benefits
                  ? { filed_unemployment_benefits: values.filed_unemployment_benefits === 'yes' }
                  : {}),
              };
              trackEvent('ONBOARDING_LOCATION_NEXT');
              apiRequest('PUT', '/users/me/location/', {
                data: valuesToSubmit,
              })
                .then(() => {
                  // Update cache immediately so back navigation can pre-populate
                  queryClient.setQueryData(['/users/me/location/'], (old) => ({
                    ...old,
                    ...valuesToSubmit,
                  }));
                  if (values.filed_unemployment_benefits === 'yes') {
                    history.push(`${urlPrefix}/onboarding/ui-claimant`);
                  } else {
                    history.push(`${urlPrefix}/onboarding/goals`);
                  }
                })
                .catch(() => {
                  setSubmitting(false);
                });
            }}
          >
            {({ values, errors, touched, handleChange, handleSubmit, setFieldValue, setFieldTouched }) => {
              const effectiveCity = values.current_city ?? autoCity;
              const effectiveState = effectiveCity?.state_id || '';
              return (
                <>
                  <form onSubmit={handleSubmit}>
                    <Typography
                      align="center"
                      component="h2"
                      sx={{
                        color: 'rgb(66, 66, 66)',
                        mb: 3,
                        fontSize: '16px',
                        fontWeight: 700,
                        fontFamily: 'Avenir, Arial, sans-serif',
                        lineHeight: '24px',
                        display: 'block',
                        px: 1,
                      }}
                    >
                      {intl.formatMessage({ id: 'onboarding.location.citySubtitle.line1' })}
                      <br />
                      {intl.formatMessage({ id: 'onboarding.location.citySubtitle.line2' })}
                      <br />
                      {intl.formatMessage({ id: 'onboarding.location.citySubtitle.line3' })}
                    </Typography>
                    <Box mb={3} />
                    <Box sx={{ display: 'flex', mb: 1, flexDirection: 'column' }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {intl.formatMessage({ id: 'onboarding.location.zipCode' })}
                        <Typography sx={{ color: (theme) => theme.palette.error.main }} component="span">
                          *
                        </Typography>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <TextField
                          id="zip-code-onboard-input"
                          name="zip_code"
                          variant="filled"
                          label={intl.formatMessage({ id: 'forms.onboarding.zipcode' })}
                          value={values.zip_code}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setFieldValue('zip_code', val);
                            setFieldTouched('zip_code', true, false);
                            if (/^\d{5}$/.test(val)) {
                              setCityFilterZip(val);
                              setFieldValue('current_city', null, false);
                            } else {
                              setCityFilterZip('');
                              setFieldValue('current_city', null, false);
                            }
                          }}
                          helperText={touched.zip_code && errors.zip_code ? errors.zip_code : ''}
                          error={touched.zip_code && Boolean(errors.zip_code)}
                          inputProps={{ maxLength: 5, inputMode: 'numeric', pattern: '[0-9]*' }}
                          sx={{
                            flex: 1,
                            '.MuiFilledInput-input:not(.Mui-disabled):not(:hover)': { backgroundColor: 'white' },
                          }}
                        />
                        <Autocomplete
                          id="city-onboard-autocomplete"
                          options={filteredCities}
                          getOptionLabel={(opt) => opt.city || ''}
                          getOptionKey={(opt) => opt.id}
                          isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                          value={effectiveCity}
                          onChange={(_, value) => setFieldValue('current_city', value)}
                          sx={{
                            flex: 2,
                            '.MuiFilledInput-root:not(.Mui-disabled):not(:hover)': { backgroundColor: 'white' },
                          }}
                          renderInput={(params) => (
                            <TextField {...params} variant="filled" label="City" id="city-onboard-input" />
                          )}
                          classes={{ paper: 'autocomplete-paper-border' }}
                        />
                        <TextField variant="filled" label="State" value={effectiveState} disabled sx={{ width: 100 }} />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', mb: 1, flexDirection: 'column' }}>
                      {/* Distance */}
                      <Typography sx={{ fontWeight: 700 }}>
                        {intl.formatMessage({ id: 'onboarding.location.distance' })}
                        <Typography sx={{ color: (theme) => theme.palette.error.main }} component="span">
                          *
                        </Typography>
                      </Typography>
                      <Autocomplete
                        id="travel-for-work-onboard-dropdown"
                        options={distanceOptions}
                        getOptionLabel={(option) => option.label}
                        getOptionKey={(option) => option.id}
                        value={values.desired_commute_mi ?? null}
                        sx={{
                          '.MuiFilledInput-root:not(.Mui-disabled):not(:hover)': { backgroundColor: 'white' },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="filled"
                            label={intl.formatMessage({ id: 'onboarding.location.distanceLabel' })}
                            helperText={
                              touched.desired_commute_mi && errors.desired_commute_mi ? errors.desired_commute_mi : ''
                            }
                            error={touched.desired_commute_mi && Boolean(errors.desired_commute_mi)}
                            id="travel-for-work-input-id"
                          />
                        )}
                        onChange={(_, value) => setFieldValue('desired_commute_mi', value)}
                        classes={{
                          paper: 'autocomplete-paper-border',
                        }}
                      />
                    </Box>
                    {/* Willing to relocate */}
                    <Box sx={{ display: 'flex', mb: 1, flexDirection: 'column' }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {intl.formatMessage({ id: 'onboarding.location.willingToRelocate' })}
                        <Typography sx={{ color: (theme) => theme.palette.error.main }} component="span">
                          *
                        </Typography>
                      </Typography>
                      <FormControl error={touched.willing_to_relocate && Boolean(errors.willing_to_relocate)}>
                        <RadioGroup
                          aria-label={intl.formatMessage({ id: 'onboarding.location.willingToRelocate' })}
                          name="willing_to_relocate"
                          value={values.willing_to_relocate}
                          onChange={handleChange}
                          row
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio color="primary" id="yes-relocate-onboard-radio-button" />}
                            label={intl.formatMessage({ id: 'jobSearchActivity.form.yes' })}
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio color="primary" id="no-relocate-onboard-radio-button" />}
                            label={intl.formatMessage({ id: 'jobSearchActivity.form.no' })}
                          />
                        </RadioGroup>
                        {touched.willing_to_relocate && errors.willing_to_relocate && (
                          <FormHelperText sx={{ left: 0, right: 'auto' }}>
                            {intl.formatMessage({ id: errors.willing_to_relocate })}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Box>

                    {/* Relocation Cities - shown when willing to relocate is yes */}
                    <Box
                      sx={{
                        display: values.willing_to_relocate === 'yes' ? 'flex' : 'none',
                        mb: 1,
                        flexDirection: 'column',
                      }}
                    >
                      <Typography sx={{ fontWeight: 700 }}>
                        {intl.formatMessage({ id: 'onboarding.location.relocationCityLabel' })}
                        <Typography sx={{ color: (theme) => theme.palette.error.main }} component="span">
                          *
                        </Typography>
                      </Typography>
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        id="relocation-city-onboard-dropdown"
                        options={ARKANSAS_CITIES}
                        getOptionLabel={({ city }) => city}
                        getOptionKey={({ id }) => id}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        value={values.alternate_cities}
                        renderTags={(value, getTagProps) => (
                          <div>
                            {value.map((option, index) => {
                              const { className, ...rest } = getTagProps({ index }); //eslint-disable-line
                              return (
                                <Chip
                                  key={option.id}
                                  size="small"
                                  label={option.city}
                                  {...rest}
                                  sx={{ maxWidth: 180, px: 0, my: 0.5 }}
                                />
                              );
                            })}
                          </div>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="filled"
                            placeholder={intl.formatMessage({ id: 'onboarding.location.relocationCityPlaceholder' })}
                            helperText={
                              touched.alternate_cities && errors.alternate_cities ? errors.alternate_cities : ''
                            }
                            error={touched.alternate_cities && Boolean(errors.alternate_cities)}
                            id="relocation-city-input"
                          />
                        )}
                        onChange={(_, value) => setFieldValue('alternate_cities', value)}
                        classes={{
                          paper: 'autocomplete-paper-border',
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            paddingTop: '18px!important',
                          },
                          '& .MuiAutocomplete-input': {
                            fontSize: 12,
                          },
                        }}
                      />
                    </Box>

                    {/* Unemployment Benefits Question */}
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {intl.formatMessage({ id: 'onboarding.location.unemploymentBenefits' })}
                        <Typography sx={{ color: (theme) => theme.palette.error.main }} component="span">
                          *
                        </Typography>
                      </Typography>
                      <FormControl
                        error={touched.filed_unemployment_benefits && Boolean(errors.filed_unemployment_benefits)}
                      >
                        <RadioGroup
                          aria-label={intl.formatMessage({ id: 'onboarding.location.unemploymentBenefits' })}
                          name="filed_unemployment_benefits"
                          value={values.filed_unemployment_benefits}
                          onChange={handleChange}
                          row
                        >
                          <FormControlLabel
                            value="yes"
                            control={<Radio color="primary" id="yes-unemployment-onboard-radio-button" />}
                            label={intl.formatMessage({ id: 'jobSearchActivity.form.yes' })}
                          />
                          <FormControlLabel
                            value="no"
                            control={<Radio color="primary" id="no-unemployment-onboard-radio-button" />}
                            label={intl.formatMessage({ id: 'jobSearchActivity.form.no' })}
                          />
                        </RadioGroup>
                        {touched.filed_unemployment_benefits && errors.filed_unemployment_benefits && (
                          <FormHelperText sx={{ left: 0, right: 'auto' }}>
                            {intl.formatMessage({ id: errors.filed_unemployment_benefits })}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Box>
                  </form>
                  <OnboardingNavigationButtons
                    onBack={handleBack}
                    onNext={handleSubmit}
                    disabled={submitting}
                    loading={submitting}
                    backId="back-onboard-step-4-button"
                    nextId="next-onboard-step-5-button"
                  />
                </>
              );
            }}
          </Formik>
        </OnboardingContentWrapper>
      </Box>
    </Box>
  );
}
